import React, { useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Link, Typography, useTheme } from "@mui/material";
import { ISearchResultsItem, ISearchResultsItemsGroup } from "../../services/object-search-data-service/Models";
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getDeviceRoute, getGroupRoute, getTagRoute, getTankBatteryRoute, getTankRoute, getWellRoute } from "../../common/utilities/RouteHelpers";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { getRouteContext } from "../../hooks/use-route-context/UseRouteContextUtils";
import { NavigationSource } from "../../common/enums/NavigationSource";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import { useUserDefaultViews } from "../../queries/user-default-view/UseUserDefaultViews";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { isMobile, isTablet } from "react-device-detect";
import { routesByViewID } from "../../routes/routes";

export type SearchViewItemProps = {
    header: string,
    subHeader: string,
    searchText: string,
    items: ISearchResultsItemsGroup[]
}

export default function SearchViewItemList({ header, subHeader, searchText, items }: SearchViewItemProps) {
    const [userState] = useUserContext();
    const navigateTo = useNavigateTo();
    const theme = useTheme();
    const isPhone = isMobile && !isTablet;

    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
    });


    const groupViewID = useMemo(() => {
        return defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                            o.user?.id === userState.user?.id)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                     !o.user)?.viewID ?? 
                UIViewEnum.Group_SummaryListing;

    }, [
        defaultViews,
        userState.user?.id
    ]);

    const deviceViewID = useMemo(() => {
        return defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                        o.user?.id === userState.user?.id)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                        !o.user)?.viewID ??
                    UIViewEnum.Device_Details;
    }, [
        defaultViews,
        userState.user?.id
    ]);


    const onNavigate = (item: ISearchResultsItem) => {

        let url: string | undefined;

        //const params = new URLSearchParams(location.search);
        const queryString = item.url.replace(/\/redirect/g, "");

        const routeContext = getRouteContext(UIViewEnum.None, queryString);

        if (routeContext) {
            switch (routeContext.contextObject?.objectType) {

                case eLynxObjectTypeEnum.Group: {
                        const route = routesByViewID.get(groupViewID);

                        url = getGroupRoute(isPhone && !route.allowMobile ? 
                                            UIViewEnum.Group_SummaryListing : 
                                            groupViewID, 
                                            routeContext.contextObject.id);
                    }
                    break;

                case eLynxObjectTypeEnum.TankBattery:
                    url = getTankBatteryRoute(UIViewEnum.TankBattery_Details, routeContext.groupID, routeContext.contextObject.id);
                    break;

                case eLynxObjectTypeEnum.Well:
                    url = getWellRoute(UIViewEnum.Well_Details, routeContext.groupID, routeContext.contextObject.id);
                    break;

                case eLynxObjectTypeEnum.Device: {
                        const route = routesByViewID.get(deviceViewID);

                        url = getDeviceRoute(isPhone && !route.allowMobile ? 
                                    UIViewEnum.Device_Details_Values : 
                                    deviceViewID, 
                                    routeContext.groupID, 
                                    routeContext.wellID, 
                                    routeContext.contextObject.id);
                    }
                    break;

                case eLynxObjectTypeEnum.Tank:
                    url = getTankRoute(UIViewEnum.Tank_Details, routeContext.groupID, routeContext.deviceID, routeContext.tankBatteryID, routeContext.contextObject.id);
                    break;

                case eLynxObjectTypeEnum.Tag:
                    url = getTagRoute(UIViewEnum.Tag_Details, routeContext.groupID, routeContext.wellID, routeContext.deviceID, routeContext.tankBatteryID, routeContext.tankID, routeContext.tagID);
                    break;

                default:
                    throw new Error(`search: unsupported redirect ${routeContext.contextObject?.objectType}`);
            }
        }

        if (url) {
            url = `${url}&navSrc=${NavigationSource.SearchResults}`
            navigateTo(url);
        }
    }

    return (

        <Box sx={{marginLeft: theme.spacing(1), marginRight: theme.spacing(1)}}>
            <Typography sx={{marginTop: theme.spacing(2), paddingLeft: theme.spacing(1)}}
                variant='h6'>
                {`${header} '${searchText}'`}
            </Typography>

            {items.map((o, index) =>

                <Accordion key={index}
                    sx={{
                        '& + &': {
                            marginTop: theme.spacing(2)
                        }
                    }}>
                    <AccordionSummary
                        id="search-results-header"
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="search-results-content">

                        <Box sx={{ flexBasis: '100%' }}>
                            {o.label &&
                                <Typography variant='subtitle2'>
                                    {o.label}
                                </Typography>
                            }
                            <Typography variant='subtitle2'>
                                {`${subHeader} [${o.items.length}]`}
                            </Typography>

                        </Box>

                    </AccordionSummary>

                    <AccordionDetails id='search-results-content' sx={{display: 'flex', flexDirection: 'column', marginTop: 0, paddingLeft: theme.spacing(3)}}>
                        {
                                o.items.map((v, index) =>
                                <Link key={index}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                        },
                                        '& + &': {
                                            marginTop: theme.spacing(1)
                                        }
                                    }}
                                    variant="body2"
                                    aria-label={`navigate to ${v.description}`}
                                    onClick={() => onNavigate(v)}>
                                    {v.description}
                                </Link>)
                        }
                    </AccordionDetails>
                </Accordion>

            )}
        </Box>

    );
}
